import type { Page } from '../../app/feature';
import { esc, inputValue, q } from '../../shared/dom';
import { t } from '../../shared/i18n';
import { dateTime, fmt, money } from '../../shared/format';
import { ApiError } from '../../shared/api/http';
import { toast } from '../../shared/ui/toast';
import { href } from '../../app/router';
import { isAdmin } from '../../shared/session/session';
import { hostsApi, type HostSummary } from './hosts.api';

let list: HostSummary[] = [];
let loaded = false;

/** Influenciadores: quem conduz as lives, com o sorteio atual e o que já fizeram. */
export const hosts: Page = {
  id: 'influencers',
  pattern: '/influencers',
  roles: ['ADMIN', 'SUPPORT'],
  nav: { key: 'nav.hosts', icon: '🎙', group: 'ops' },

  template: () => `
<div class="view-head">
  <div>
    <h2 data-i18n="hs.title">Influencers</h2>
    <p data-i18n="hs.sub">Quienes conducen las lives. Cada uno entra con su propio acceso y ve sólo el estudio del sorteo que tiene asignado. Aquí ves quién está en qué sorteo y el historial de cada uno.</p>
  </div>
  <div class="spacer"></div>
  <a class="btn btn-primary" id="hs-new" href="${href('/accesos')}">+ <span data-i18n="hs.new">Nuevo influencer</span></a>
</div>

<div class="cols" style="margin-bottom:18px">
  <div class="kpi span-3" style="--accent:var(--orange)"><div class="k-label" data-i18n="hs.kTotal">Influencers</div><div class="k-val tnum" id="hk-total">0</div><div class="k-sub" id="hk-total-sub">—</div></div>
  <div class="kpi span-3" style="--accent:var(--green)"><div class="k-label" data-i18n="hs.kAssigned">Con sorteo asignado</div><div class="k-val tnum" id="hk-assigned">0</div><div class="k-sub" data-i18n="hs.kAssignedSub">listos para la próxima live</div></div>
  <div class="kpi span-3" style="--accent:var(--gold)"><div class="k-label" data-i18n="hs.kDraws">Premios sorteados</div><div class="k-val tnum" id="hk-draws">0</div><div class="k-sub" data-i18n="hs.kDrawsSub">en todas las lives</div></div>
  <div class="kpi span-3" style="--accent:var(--line-2)"><div class="k-label" data-i18n="hs.kPrizes">Valor entregado</div><div class="k-val tnum" id="hk-prizes" style="font-size:26px">$0</div><div class="k-sub" data-i18n="hs.kPrizesSub">suma de los premios sorteados</div></div>
</div>

<div class="panel-head">
  <h3 data-i18n="hs.list">Influencers</h3>
  <div class="spacer"></div>
  <input type="text" id="hs-search" placeholder="Buscar por nombre, @usuario o e-mail…" style="max-width:300px">
</div>
<div class="tw">
  <table id="tbl-hosts">
    <thead><tr>
      <th data-i18n="hs.thWho">Influencer</th>
      <th data-i18n="hs.thCurrent">Sorteo actual</th>
      <th data-i18n="hs.thEvents">Sorteos</th>
      <th data-i18n="hs.thDraws">Premios</th>
      <th data-i18n="hs.thPrizes">Valor entregado</th>
      <th data-i18n="users.lastLogin">Último acceso</th>
      <th data-i18n="th.status">Estado</th>
      <th></th>
    </tr></thead>
    <tbody></tbody>
  </table>
</div>`,

  mount() {
    q<HTMLInputElement>('#hs-search').oninput = () => renderTable();
  },

  render() {
    q('#hs-new').hidden = !isAdmin();
    renderTable();
    void load();
  }
};

async function load(): Promise<void> {
  try { list = await hostsApi.list(); loaded = true; renderTable(); }
  catch (err) { toast(err instanceof ApiError ? err.message : t('auth.errGeneric')); }
}

function renderTable(): void {
  const s = inputValue('#hs-search').trim().toLowerCase();
  const rows = list.filter(h => !s || [h.name, h.handle ?? '', h.email].some(v => v.toLowerCase().includes(s)));

  q('#hk-total').textContent = fmt(list.length);
  q('#hk-total-sub').textContent = `${fmt(list.filter(h => h.active).length)} ${t('hs.active')}`;
  q('#hk-assigned').textContent = fmt(list.filter(h => h.currentEvent).length);
  q('#hk-draws').textContent = fmt(list.reduce((a, h) => a + h.drawsCount, 0));
  q('#hk-prizes').textContent = money(list.reduce((a, h) => a + h.prizesTotal, 0));

  q('#tbl-hosts tbody').innerHTML = rows.map(h => `<tr class="${h.active ? '' : 'muted'}">
    <td>
      <a class="auth-link" href="${href('/influencers/' + h.id)}"><b>${esc(h.name)}</b></a>${h.handle ? ` <span class="muted">${esc(h.handle)}</span>` : ''}
      <div class="muted" style="font-size:12px">${esc(h.email)}</div>
    </td>
    <td>${h.currentEvent
      ? `<a class="auth-link" href="${href('/sorteos/' + h.currentEvent.id)}">${esc(h.currentEvent.name)}</a><div class="muted" style="font-size:11.5px">${esc(h.currentEvent.scheduledAt ? dateTime(Date.parse(h.currentEvent.scheduledAt)) : t('ov.noDate'))}</div>`
      : `<span class="chip chip-gold">${esc(t('hs.noEvent'))}</span>`}</td>
    <td class="tnum">${fmt(h.eventsHosted)}</td>
    <td class="tnum">${fmt(h.drawsCount)}</td>
    <td class="tnum">${money(h.prizesTotal)}</td>
    <td class="muted tnum" style="font-size:12px">${h.lastLoginAt ? dateTime(Date.parse(h.lastLoginAt)) : '—'}</td>
    <td><span class="chip ${h.active ? 'chip-green' : 'chip-dim'}">${h.active ? t('ev.on') : t('ev.off')}</span></td>
    <td style="text-align:right"><a class="btn btn-sm" href="${href('/influencers/' + h.id)}">${esc(t('pl.open'))}</a></td>
  </tr>`).join('') || `<tr><td colspan="8" class="empty">${loaded ? esc(t('hs.none')) : '…'}</td></tr>`;
}
